import { Criticidad, type CriticidadValor } from '../../domain/value-objects/Criticidad';
import { registrarError, traducirErrorSupabase } from '../../domain/errores';
import { aBooleano, aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { filtroDemo } from './FiltroDemo';

/**
 * Vías por las que se puede avisar a alguien.
 *
 * Tener el dato y querer recibir avisos por ahí son dos cosas distintas: un
 * contacto puede tener WhatsApp y no querer que le escriban de madrugada. La
 * base lo guarda como un objeto JSON con una marca por vía.
 */
export interface CanalesAviso {
  email: boolean;
  whatsapp: boolean;
  telegram: boolean;
}

/**
 * Lee el objeto de canales tal como lo devuelve la base.
 *
 * Cuando falta —contactos creados antes de que el campo existiera— se dan por
 * habilitadas las tres vías: es lo que venían haciendo, y presentarlas como
 * apagadas haría creer que un contacto que sí recibe avisos no los recibe.
 */
function aCanales(valor: unknown): CanalesAviso {
  const bruto =
    typeof valor === 'object' && valor !== null ? (valor as Record<string, unknown>) : {};
  const leer = (clave: string) => (clave in bruto ? bruto[clave] === true : true);

  return {
    email: leer('email'),
    whatsapp: leer('whatsapp'),
    telegram: leer('telegram'),
  };
}

/**
 * Persona a la que avisar cuando salta una alarma.
 *
 * Un contacto recibe avisos por cada vía de la que tenga dato y que además
 * tenga habilitada.
 */
export interface ContactoAlerta {
  id: number;
  nombre: string;
  clienteId: number | null;
  clienteNombre: string | null;
  email: string | null;
  whatsapp: string | null;
  telegram: string | null;
  cargo: string | null;
  notas: string | null;
  canales: CanalesAviso;
  activo: boolean;
}

export interface DatosContacto {
  nombre: string;
  cliente_id: number;
  email: string | null;
  telefono_whatsapp: string | null;
  telegram_chat_id: string | null;
  rol_cargo: string | null;
  notas: string | null;
  canales_habilitados: CanalesAviso;
  activo: boolean;
}

/** Nivel de una cadena de avisos: a quién se recurre y cuándo. */
export interface PoliticaEscalamiento {
  id: number;
  clienteId: number | null;
  clienteNombre: string | null;
  nivel: number;
  severidadMinima: Criticidad;
  activo: boolean;
  /** Por qué vías avisa este nivel. */
  canales: CanalesAviso;
  /** Cuánto se espera antes de volver a intentarlo, en segundos. */
  intervaloIntentosSegundos: number | null;
  /** Contactos a los que avisa este nivel. */
  destinatarios: ContactoAlerta[];
}

export interface DatosPolitica {
  cliente_id: number;
  nivel_escalacion: number;
  severidad_minima: CriticidadValor;
  canales: CanalesAviso;
  intervalo_intentos_seg: number;
  activo: boolean;
}

const TABLA_CONTACTOS = 'contactos_alerta';
const VISTA_CONTACTOS = 'vw_contactos_alerta_detalle';
const TABLA_POLITICAS = 'v2_politicas_escalamiento';
const TABLA_DESTINATARIOS = 'v2_politicas_escalamiento_destinatarios';

function aContacto(fila: Record<string, unknown>): ContactoAlerta {
  return {
    id: aNumero(fila.contacto_id) ?? aNumero(fila.id) ?? 0,
    nombre: aTexto(fila.contacto_nombre) ?? aTexto(fila.nombre) ?? 'Sin nombre',
    clienteId: aNumero(fila.cliente_id),
    clienteNombre: aTexto(fila.cliente_nombre),
    email: aTexto(fila.email),
    whatsapp: aTexto(fila.telefono_whatsapp),
    telegram: aTexto(fila.telegram_chat_id),
    cargo: aTexto(fila.rol_cargo),
    notas: aTexto(fila.notas),
    canales: aCanales(fila.canales_habilitados),
    activo: aBooleano(fila.activo) ?? true,
  };
}

/**
 * Contactos de alerta y cadenas de escalamiento.
 *
 * Ambos responden a la misma pregunta —a quién se avisa cuando algo va mal—
 * así que comparten repositorio: las políticas no significan nada sin sus
 * contactos y siempre se leen juntas.
 */
export class RepositorioNotificaciones {
  async listarContactos(clienteId?: number | null): Promise<ContactoAlerta[]> {
    let consulta = filtroDemo.aplicar(VISTA_CONTACTOS, supabase.from(VISTA_CONTACTOS).select('*'));
    consulta = ambitoSesion.aplicar(consulta);
    if (clienteId) consulta = consulta.eq('cliente_id', clienteId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[])
      .map(aContacto)
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  async crearContacto(datos: DatosContacto): Promise<void> {
    const { error } = await supabase.from(TABLA_CONTACTOS).insert(datos);
    if (error) throw traducirErrorSupabase(error);
  }

  async actualizarContacto(id: number, datos: Partial<DatosContacto>): Promise<void> {
    const { error } = await supabase.from(TABLA_CONTACTOS).update(datos).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async eliminarContacto(id: number): Promise<void> {
    const { error } = await supabase.from(TABLA_CONTACTOS).delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async cambiarActivoContacto(id: number, activo: boolean): Promise<void> {
    return this.actualizarContacto(id, { activo });
  }

  /** Políticas con sus destinatarios ya resueltos, ordenadas por nivel. */
  async listarPoliticas(clienteId?: number | null): Promise<PoliticaEscalamiento[]> {
    let consulta = filtroDemo.aplicar(TABLA_POLITICAS, supabase.from(TABLA_POLITICAS).select('*'));
    consulta = ambitoSesion.aplicar(consulta);
    if (clienteId) consulta = consulta.eq('cliente_id', clienteId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    const filas = (data ?? []) as Record<string, unknown>[];
    if (filas.length === 0) return [];

    const ids = filas.map((f) => aNumero(f.id)).filter((id): id is number => id !== null);
    const [destinatarios, contactos] = await Promise.all([
      this.leerDestinatarios(ids),
      this.listarContactos(clienteId),
    ]);

    const porId = new Map(contactos.map((c) => [c.id, c]));

    return filas
      .map((fila) => {
        const id = aNumero(fila.id) ?? 0;

        return {
          id,
          clienteId: aNumero(fila.cliente_id),
          clienteNombre: null,
          nivel: aNumero(fila.nivel_escalacion) ?? 1,
          severidadMinima: Criticidad.from(aTexto(fila.severidad_minima)),
          canales: aCanales(fila.canales),
          intervaloIntentosSegundos: aNumero(fila.intervalo_intentos_seg),
          activo: aBooleano(fila.activo) ?? true,
          destinatarios: (destinatarios.get(id) ?? [])
            .map((contactoId) => porId.get(contactoId))
            .filter((c): c is ContactoAlerta => c !== undefined),
        };
      })
      .sort((a, b) => a.nivel - b.nivel);
  }

  private async leerDestinatarios(ids: readonly number[]): Promise<Map<number, number[]>> {
    const mapa = new Map<number, number[]>();
    if (ids.length === 0) return mapa;

    const { data, error } = await supabase
      .from(TABLA_DESTINATARIOS)
      .select('politica_escalamiento_id, contacto_id')
      .in('politica_escalamiento_id', [...ids]);

    if (error) {
      registrarError('notificaciones.destinatarios', error);
      return mapa;
    }

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const politicaId = aNumero(fila.politica_escalamiento_id);
      const contactoId = aNumero(fila.contacto_id);
      if (politicaId === null || contactoId === null) continue;

      mapa.set(politicaId, [...(mapa.get(politicaId) ?? []), contactoId]);
    }

    return mapa;
  }

  /** Crea la política y deja asignados sus destinatarios. */
  async crearPolitica(datos: DatosPolitica, contactoIds: readonly number[]): Promise<void> {
    const { data, error } = await supabase
      .from(TABLA_POLITICAS)
      .insert(datos)
      .select('id')
      .single();

    if (error) throw traducirErrorSupabase(error);

    const id = aNumero((data as Record<string, unknown>).id);
    if (id !== null) await this.guardarDestinatarios(id, contactoIds);
  }

  async actualizarPolitica(
    id: number,
    datos: Partial<DatosPolitica>,
    contactoIds?: readonly number[],
  ): Promise<void> {
    const { error } = await supabase.from(TABLA_POLITICAS).update(datos).eq('id', id);
    if (error) throw traducirErrorSupabase(error);

    if (contactoIds) await this.guardarDestinatarios(id, contactoIds);
  }

  async eliminarPolitica(id: number): Promise<void> {
    const { error } = await supabase.from(TABLA_POLITICAS).delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async cambiarActivoPolitica(id: number, activo: boolean): Promise<void> {
    const { error } = await supabase.from(TABLA_POLITICAS).update({ activo }).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Reemplaza los destinatarios de una política.
   * Se borran y se vuelven a insertar porque la relación no tiene datos
   * propios: sólo dice qué contacto pertenece a qué nivel.
   */
  private async guardarDestinatarios(
    politicaId: number,
    contactoIds: readonly number[],
  ): Promise<void> {
    const { error: errorBorrado } = await supabase
      .from(TABLA_DESTINATARIOS)
      .delete()
      .eq('politica_escalamiento_id', politicaId);

    if (errorBorrado) throw traducirErrorSupabase(errorBorrado);
    if (contactoIds.length === 0) return;

    const { error } = await supabase.from(TABLA_DESTINATARIOS).insert(
      contactoIds.map((contactoId) => ({
        politica_escalamiento_id: politicaId,
        contacto_id: contactoId,
      })),
    );

    if (error) throw traducirErrorSupabase(error);
  }
}

export const repositorioNotificaciones = new RepositorioNotificaciones();
